import { t } from "@lingui/macro";
import { FileText, Mail } from "lucide-react";
import { Link } from "react-router-dom";

import { FirstRoundLegalNotice } from "../first-round-notice";

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">{title}</h2>
    <div className="space-y-4 text-gray-600 leading-relaxed">{children}</div>
  </section>
);

const Sub = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mt-4">
    <h3 className="font-medium text-gray-800 mb-2">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Highlight = ({ children, variant = "blue" }: { children: React.ReactNode; variant?: "blue" | "amber" | "red" }) => {
  const styles = {
    blue: "bg-sky-50 border-sky-200 text-sky-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red: "bg-red-50 border-red-200 text-red-900",
  };
  return (
    <div className={`border rounded-lg p-4 text-sm ${styles[variant]}`}>
      {children}
    </div>
  );
};

const toc = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "services", label: "Description of Services" },
  { id: "eligibility", label: "Eligibility & Registration" },
  { id: "ai-consent", label: "AI Services & Your Consent" },
  { id: "interview-terms", label: "Interview Terms" },
  { id: "spotlight-terms", label: "Spotlight Intro Terms" },
  { id: "resume-terms", label: "Resume Data Terms" },
  { id: "recruiter-access", label: "Recruiter Access & Data Portability" },
  { id: "ip", label: "Intellectual Property" },
  { id: "prohibited", label: "Prohibited Conduct" },
  { id: "payments", label: "Payments & Subscriptions" },
  { id: "deletion-rights", label: "Deletion & Your Rights" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "termination", label: "Termination" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact Us" },
];

export const TermsOfServicePage = () => {
  const EFFECTIVE_DATE = "15 May 2026";
  const SUPPORT_EMAIL = "support@theresume.io";
  const COMPANY_NAME = "theResume (TalentForge Innovations Pty Ltd)";

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-7 h-7 text-sky-500 shrink-0" />
          <h1 className="text-3xl font-bold text-gray-900">{t`Terms of Service`}</h1>
        </div>
        <p className="text-sm text-gray-500 ml-10">
          {t`Effective date:`} {EFFECTIVE_DATE} · {t`Last updated:`} {EFFECTIVE_DATE}
        </p>
        <FirstRoundLegalNotice />
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 flex gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-10 bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="space-y-1">
              {toc.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="block w-full text-left text-sm text-gray-600 hover:text-sky-600 py-1 transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <Section id="acceptance" title="1. Acceptance of Terms">
            <p>
              These Terms of Service ("<strong>Terms</strong>") are a legal agreement between you
              ("<strong>User</strong>", "<strong>Candidate</strong>", or "<strong>you</strong>") and{" "}
              <strong>{COMPANY_NAME}</strong> ("<strong>theResume</strong>", "<strong>we</strong>", or "<strong>us</strong>").
            </p>
            <p>
              By creating an account, completing your profile, or using any feature of the theResume
              platform at <strong>theresume.io</strong> (the "<strong>Services</strong>"), you confirm that
              you have read, understood, and agree to be bound by these Terms and our{" "}
              <Link to="/privacy" className="text-sky-600 hover:underline">Privacy Policy</Link>, which
              is incorporated into these Terms by reference.
            </p>
            <p>
              You accept these Terms by ticking the acceptance box when you create your account. That
              acceptance includes your consent to the AI processing described in{" "}
              <a href="#ai-consent" className="text-sky-600 hover:underline">Section 4</a> and your
              acknowledgement of the recruiter access limits described in{" "}
              <a href="#recruiter-access" className="text-sky-600 hover:underline">Section 8</a>, and
              your agreement to receive occasional emails from us as described in{" "}
              <a href="#eligibility" className="text-sky-600 hover:underline">Section 3</a>.
            </p>
            <p>
              If you do not agree to these Terms, you must not use the Services.
            </p>
          </Section>

          <Section id="services" title="2. Description of Services">
            <p>theResume provides an AI-powered career platform that includes:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li><strong>Resume Builder</strong> — Create, edit, and export professional resumes with AI assistance</li>
              <li><strong>AI Resume Analysis</strong> — ATS scoring, job-tailored improvements, and skills gap analysis</li>
              <li><strong>AI Interview</strong> — AI-conducted video/voice interviews that assess your responses and generate structured feedback</li>
              <li><strong>Practice Interviews</strong> — Self-paced practice interview sessions with coaching feedback</li>
              <li><strong>Spotlight Intro</strong> — A recorded AI-assisted video introduction indexed for recruiter discovery</li>
              <li><strong>Job Search & Applications</strong> — Discover and apply for jobs, with application tracking</li>
              <li><strong>Recruiter Platform</strong> — Tools enabling recruiters to discover, evaluate, and communicate with candidates</li>
            </ul>
          </Section>

          <Section id="eligibility" title="3. Eligibility & Account Registration">
            <p>You must be at least 16 years old to use the Services. By creating an account, you represent
            that you meet this requirement.</p>
            <p>You are responsible for maintaining the confidentiality of your account. You must notify us
            immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a> if
            you suspect unauthorised access to your account.</p>
            <p>You must provide accurate information during registration and keep it current. Accounts
            created with false information may be suspended.</p>
            <p>
              <strong>Emails from us.</strong> By accepting these Terms you agree that we may send you
              occasional emails from theResume and from First Round, our interview practice service, such
              as product updates, interview tips and offers. We will never spam you: we do not sell or
              share your email address with other businesses for their marketing, and every marketing
              email has an unsubscribe link that takes effect promptly. You can also unsubscribe at any
              time by emailing{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a>.
              Unsubscribing does not stop service emails, such as login links, receipts and security notices.
            </p>
          </Section>

          <Section id="ai-consent" title="4. AI Services & Your Consent">
            <Highlight variant="blue">
              This section describes how AI processes your data across the platform. By enabling any AI
              feature, you consent to the processing described here.
            </Highlight>

            <Sub title="4.1 AI Resume Processing">
              <p>
                When you use AI-powered resume features (improvement suggestions, ATS analysis, job tailoring),
                you consent to your resume content — including your work history, skills, education, and
                contact details — being sent to third-party AI providers (OpenAI and/or Groq) for analysis
                and generation of suggestions.
              </p>
            </Sub>

            <Sub title="4.2 AI Interview Processing">
              <p>By starting an AI interview, you explicitly consent to:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Your audio and video being captured in real-time and transmitted to LiveKit (video infrastructure) and OpenAI's Realtime API (voice processing)</li>
                <li>Your spoken responses being transcribed and stored as text</li>
                <li>Your transcript and responses being analysed by AI to generate scores, feedback, and coaching insights</li>
                <li>The full session (audio, video, transcript, and analysis) being stored and associated with your account</li>
                <li>This data being shared with the recruiter who initiated the interview (if applicable)</li>
              </ul>
            </Sub>

            <Sub title="4.3 Spotlight Intro Processing">
              <p>By recording a Spotlight Intro, you explicitly consent to:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Your video being recorded and stored in AWS S3</li>
                <li>Your speech being transcribed in real-time by AI (OpenAI Realtime API)</li>
                <li>The transcript and a summary being indexed in a search system (Elasticsearch) so that recruiters can discover you based on your spoken introduction</li>
                <li>Recruiters being able to watch your Spotlight Intro video when they view your profile</li>
              </ul>
            </Sub>

            <Sub title="4.4 No AI Model Training">
              <p>
                We <strong>do not use your personal data to train AI models</strong>. We do not sell or
                licence your resume content, interview recordings, or personal information to AI training
                datasets. Our AI providers (OpenAI, Groq) process your data under API terms that
                similarly prohibit training on API inputs by default.
              </p>
            </Sub>
          </Section>

          <Section id="interview-terms" title="5. Interview Terms">
            <Sub title="5.1 Conduct During Interviews">
              <p>During AI-powered interviews, you agree to:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Present yourself honestly and accurately</li>
                <li>Not use external assistance, scripts, or AI tools to answer questions on your behalf</li>
                <li>Not attempt to manipulate or deceive the AI interviewer system</li>
                <li>Not record, distribute, or reproduce the interview content without our prior consent</li>
              </ul>
            </Sub>

            <Sub title="5.2 Interview Recordings & Scores">
              <p>
                Interview recordings, transcripts, and AI-generated scores are shared with the recruiter
                who initiated the interview. These are used to make hiring decisions. The accuracy and
                completeness of your responses are your responsibility.
              </p>
              <p className="text-sm mt-2">
                AI scores are one input into a recruiter's decision. Hiring decisions are ultimately made
                by human recruiters, not solely by AI scores.
              </p>
            </Sub>

            <Sub title="5.3 Technical Issues">
              <p>
                If you experience a technical issue during an interview (e.g., connectivity failure, audio
                problems), please contact us at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a> and
                we will work with the recruiter to arrange a rescheduled session where appropriate.
              </p>
            </Sub>
          </Section>

          <Section id="spotlight-terms" title="6. Spotlight Intro Terms">
            <p>
              Your Spotlight Intro is a public-facing video introduction designed to be discoverable
              by recruiters. By publishing a Spotlight Intro, you understand and agree that:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>The video and transcript will be searchable by recruiters using the platform</li>
              <li>You are solely responsible for the content of your introduction</li>
              <li>You must not include confidential information belonging to third parties</li>
              <li>You grant theResume a licence to host, display, index, and transmit your Spotlight Intro to recruiters as part of the Services</li>
              <li>Once a recruiter has watched your intro, you cannot retract their having seen it, though you can unpublish it to prevent future views</li>
            </ul>
          </Section>

          <Section id="resume-terms" title="7. Resume Data Terms">
            <Sub title="7.1 Ownership">
              <p>You retain full ownership of the content of your resume. By storing your resume on
              theResume, you grant us a limited licence to host, process, and transmit your resume
              data solely for the purpose of providing the Services to you.</p>
            </Sub>

            <Sub title="7.2 Sharing with Recruiters">
              <p>When you apply for a job, your resume (and optionally your spotlight intro and interview
              performance) is shared with the recruiter associated with that job posting. You control
              which jobs you apply to.</p>
            </Sub>

            <Sub title="7.3 AI Processing of Resume">
              <p>AI features that process your resume (scoring, tailoring, matching) send relevant
              sections of your resume to third-party AI APIs as described in Section 4 and our
              Privacy Policy. You consent to this when you use AI features.</p>
            </Sub>
          </Section>

          <Section id="recruiter-access" title="8. Recruiter Access & Data Portability">
            <Highlight variant="amber">
              <strong>Please read this section carefully.</strong> It explains what happens to your data
              once a recruiter has accessed it.
            </Highlight>

            <p className="mt-4">
              Once a recruiter has <strong>viewed, downloaded, or exported</strong> any of the following:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>Your resume (PDF or view within the platform)</li>
              <li>Your Spotlight Intro video</li>
              <li>Your interview recording, transcript, or feedback report</li>
            </ul>
            <p className="mt-4">
              …that recruiter has a copy in their possession that is outside theResume's control.
              We cannot delete data from a recruiter's downloaded files, their own systems, or their
              memory. This is the same principle that applies when you submit a paper CV to an employer —
              they retain what they received.
            </p>
            <p className="mt-4">
              What we <strong>will</strong> do when you delete such data:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>Remove it from our platform (database, storage, and search index)</li>
              <li>Stop showing it to any new recruiters</li>
              <li>Mark it as deleted in our system</li>
            </ul>
            <p className="mt-4">
              What we <strong>cannot</strong> do:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>Retrieve or delete data already downloaded by a recruiter to their own systems</li>
              <li>Remove information from a recruiter's notes or memory</li>
            </ul>
          </Section>

          <Section id="ip" title="9. Intellectual Property">
            <Sub title="9.1 Your Content">
              <p>You retain ownership of all content you create or upload to theResume, including your
              resume data, interview responses, and spotlight intro. You grant theResume a worldwide,
              royalty-free, non-exclusive licence to host, process, and display this content solely
              for the purpose of providing the Services.</p>
            </Sub>

            <Sub title="9.2 Platform IP">
              <p>theResume and all associated technology, software, design, branding, and AI systems
              are owned by or licenced to {COMPANY_NAME}. You may not copy, reverse-engineer, or
              create derivative works of the platform.</p>
            </Sub>

            <Sub title="9.3 Resume Templates">
              <p>Resume templates provided by theResume are licenced for your personal use in creating
              and exporting resumes. You may not resell or sublicence the templates themselves.</p>
            </Sub>
          </Section>

          <Section id="prohibited" title="10. Prohibited Conduct">
            <p>You must not:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>Create false or misleading profiles or resume content</li>
              <li>Impersonate another person or entity</li>
              <li>Attempt to access another user's account or data</li>
              <li>Use bots, scrapers, or automated tools to interact with the platform</li>
              <li>Upload malicious files or attempt to compromise platform security</li>
              <li>Harass, abuse, or harm other users or recruiters through the platform</li>
              <li>Use AI tools to impersonate yourself in interviews or deceive the AI interviewer</li>
              <li>Attempt to reverse-engineer, extract, or misuse AI models or platform algorithms</li>
              <li>Use the Services for any purpose that violates applicable law</li>
            </ul>
            <p className="mt-4">
              Violation of these prohibitions may result in immediate account suspension and, where
              appropriate, referral to law enforcement.
            </p>
          </Section>

          <Section id="payments" title="11. Payments & Subscriptions">
            <Sub title="11.1 Paid Plans">
              <p>Certain features require a paid subscription or one-time purchase. Pricing is displayed
              at <Link to="/pricing" className="text-sky-600 hover:underline">theresume.io/pricing</Link>.
              All prices are listed in the applicable currency and exclude taxes unless stated otherwise.</p>
            </Sub>

            <Sub title="11.2 Auto-Renewal">
              <Highlight variant="amber">
                Subscriptions renew automatically at the end of each billing period (monthly or annually)
                at the same price, unless you cancel before the renewal date. You will receive an email
                reminder before each renewal.
              </Highlight>
            </Sub>

            <Sub title="11.3 Cancellation">
              <p>You can cancel your subscription at any time from{" "}
              <strong>Dashboard → Settings → Billing</strong>. Cancellation takes effect at the end of
              the current billing period. You will retain access to paid features until the period ends.</p>
            </Sub>

            <Sub title="11.4 Refund Policy">
              <p>
                We offer a <strong>7-day refund</strong> on monthly subscriptions and a{" "}
                <strong>14-day refund</strong> on annual subscriptions, provided you have not consumed
                substantial AI feature credits (more than 20% of the plan's AI allowance). To request
                a refund, email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a>.
              </p>
              <p className="text-sm mt-2">
                One-time feature pack purchases are non-refundable once credits have been used.
              </p>
            </Sub>

            <Sub title="11.5 Payment Processors">
              <p>Payments are processed by Stripe (global) and Razorpay (India). Your payment details
              are handled directly by these processors — theResume does not store card numbers or
              CVV codes.</p>
            </Sub>
          </Section>

          <Section id="deletion-rights" title="12. Data Deletion & Your Rights">
            <p>You have the following rights regarding your data on theResume:</p>

            <Sub title="12.1 Deleting Your Data">
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li><strong>Individual resumes:</strong> Delete from Dashboard → Resumes at any time.</li>
                <li><strong>Spotlight Intro:</strong> Delete from Dashboard → Spotlight Intro at any time.</li>
                <li><strong>Practice sessions:</strong> Delete individual sessions from your practice history.</li>
                <li><strong>Your account:</strong> Delete your entire account and all associated data from Dashboard → Settings → Danger Zone.</li>
              </ul>
            </Sub>

            <Sub title="12.2 Limitations on Deletion">
              <p>
                As described in Section 8 and our Privacy Policy, deletion from our platform does not
                extend to data already in possession of recruiters. Transaction records are retained for
                7 years for legal compliance regardless of account deletion.
              </p>
            </Sub>

            <Sub title="12.3 Data Export">
              <p>
                You can request an export of your data (resume JSON, application history, account profile)
                by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a>{" "}
                with the subject "<strong>Data Export Request</strong>". We will respond within 30 days.
              </p>
            </Sub>
          </Section>

          <Section id="liability" title="13. Limitation of Liability & Disclaimers">
            <Highlight variant="amber">
              <strong>Please read this section carefully.</strong> It limits our liability to you.
            </Highlight>

            <p className="mt-4">
              The Services are provided "<strong>as is</strong>" and "<strong>as available</strong>"
              without warranties of any kind, express or implied, including fitness for a particular
              purpose or non-infringement.
            </p>
            <p className="mt-4">
              We do not guarantee that:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>AI interview scores or resume suggestions will result in job offers</li>
              <li>The Services will be uninterrupted, error-free, or secure at all times</li>
              <li>AI-generated content will be accurate, complete, or suitable for your purposes</li>
            </ul>
            <p className="mt-4">
              To the maximum extent permitted by law, theResume's total liability for any claim arising
              from the use of the Services is limited to the amount you paid us in the 3 months
              preceding the claim.
            </p>
            <p className="mt-4 text-sm">
              Nothing in these Terms excludes liability for death or personal injury caused by our
              negligence, or for fraudulent misrepresentation.
            </p>
          </Section>

          <Section id="indemnification" title="14. Indemnification">
            <p>
              You agree to indemnify and hold harmless theResume, its officers, directors, employees,
              and agents from any claims, damages, losses, and expenses (including legal fees) arising
              from: (i) your use of the Services; (ii) your violation of these Terms; (iii) your violation
              of any third-party rights; or (iv) content you submit to the platform.
            </p>
          </Section>

          <Section id="termination" title="15. Termination">
            <p>
              We may suspend or terminate your account immediately, without notice, if we determine that
              you have violated these Terms, engaged in fraudulent activity, or posed a risk to other users
              or the platform.
            </p>
            <p className="mt-2">
              You may terminate your account at any time from Dashboard → Settings → Danger Zone.
              Upon termination, your data is handled as described in Section 12 and our Privacy Policy.
            </p>
          </Section>

          <Section id="governing-law" title="16. Governing Law & Dispute Resolution">
            <p>
              These Terms are governed by the laws of <strong>Singapore</strong>, without regard to
              conflict of law principles.
            </p>
            <p className="mt-2">
              Any disputes arising from these Terms will first be attempted to be resolved through
              good-faith negotiation. If unresolved within 30 days, disputes will be submitted to
              the courts of Singapore.
            </p>
            <p className="mt-2 text-sm text-gray-500">
              If you are a consumer in the European Union or United Kingdom, you may also have the right
              to bring proceedings in the courts of your country of residence.
            </p>
          </Section>

          <Section id="changes" title="17. Changes to These Terms">
            <p>
              We may update these Terms from time to time. When we make material changes, we will notify
              you via email and/or an in-app notice at least 14 days before the changes take effect.
              Continued use of the Services after the effective date constitutes acceptance of the
              updated Terms.
            </p>
          </Section>

          <Section id="contact" title="18. Contact Us">
            <p>
              For questions about these Terms, please contact us:
            </p>
            <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-start gap-4">
              <Mail className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">{COMPANY_NAME}</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sky-600 hover:underline text-sm"
                >
                  {SUPPORT_EMAIL}
                </a>
                <p className="text-sm text-gray-500 mt-1">
                  Subject line: <em>Terms of Service Enquiry</em>
                </p>
              </div>
            </div>
          </Section>
        </main>
      </div>
    </div>
  );
};
